// @flow
import { useState } from 'react';
import { Box, Button, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { PencilSimple, Sparkle } from '@phosphor-icons/react';
import { AiBadge, CallHistoryFieldsFragment, isAgentManager, NoData, Portal } from '../../../../../../shared';
import moment from 'moment';
import _ from 'lodash';
import { CallQa, scoreColor } from './call-qa-types';
import { QaAuditSheet } from './qa-audit-sheet';
import { QaEditModal } from './qa-edit-modal';

type Props = {
  // Typed against the shared CallHistoryFields fragment so any query that spreads it
  // (callHistoryItems for L1, callHistoryByAirCallId for L2/L3) can pass its call here.
  call?: CallHistoryFieldsFragment | null;
};

const titleCase = (s?: string | null) =>
  s ? _.upperFirst(s.toLowerCase().replace(/_/g, ' ')) : '';

// Reusable, presentational QA panel. Rendered inside the call drawer's QA tab (L1),
// and reused anywhere a call's QA is shown.
export const CallQaPanel = ({ call }: Props) => {
  const [editOpen, setEditOpen] = useState(false);
  // Call at render time (isAgentManager is a function, not a frozen const) so it reflects auth.
  const isManager = isAgentManager();

  const callQa = call?.callQa;

  // null => call has no QA (never scored / unsuccessful). Guard is purely the null check.
  if (callQa == null) {
    return <NoData>Not scored for compliance.</NoData>;
  }

  const score = callQa.effectiveScore ?? 0;
  const color = scoreColor(score);

  return (
    <Stack gap={2}>
      {/* Score headline */}
      <Portal p={2}>
        <Stack gap={1.5}>
          <Stack direction={'row'} alignItems={'flex-start'} gap={2}>
            <Stack flex={1} gap={1}>
              <Stack direction={'row'} alignItems={'center'} gap={1} flexWrap={'wrap'}>
                <Typography variant={'h1'} sx={{ color, lineHeight: 1 }}>
                  {Math.round(score)}%
                </Typography>
                <AiBadge label={'AI Scored'} />
                {callQa.dispositionClass && (
                  <Chip label={titleCase(callQa.dispositionClass)} size={'small'} sx={{ fontSize: 11 }} />
                )}
                {callQa.wasOverridden && <EditedByManagerPill qa={callQa} />}
              </Stack>
              <Typography variant={'body2'} color={'text.secondary'}>
                {callQa.pointsLost} of {callQa.applicableMax} points lost
              </Typography>
            </Stack>
            {isManager && (
              <Button
                size={'small'}
                variant={'outlined'}
                startIcon={<PencilSimple size={14} />}
                onClick={() => setEditOpen(true)}
              >
                Edit
              </Button>
            )}
          </Stack>
        </Stack>
      </Portal>

      {/* Coaching */}
      {(callQa.overallAssessment || callQa.coachingRecommendations) && (
        <Box sx={{ bgcolor: '#FAFAFA', borderLeft: '2px solid #7C3AED', borderRadius: '0 8px 8px 0', p: 2 }}>
          <Stack gap={1.5}>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <Sparkle size={14} weight={'fill'} color={'#7C3AED'} />
              <Typography variant={'caption'} sx={{ fontWeight: 700, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Coaching
              </Typography>
            </Stack>
            {callQa.overallAssessment && (
              <Typography variant={'body2'} color={'text.secondary'} whiteSpace={'pre-line'}>
                {callQa.overallAssessment}
              </Typography>
            )}
            {callQa.coachingRecommendations && (
              <Typography variant={'body2'} color={'text.secondary'} whiteSpace={'pre-line'}>
                {callQa.coachingRecommendations}
              </Typography>
            )}
            <Typography variant={'caption'} color={'text.secondary'}>
              {callQa.rubricVersion}
              {callQa.scoredAt && ` - Scored ${moment(callQa.scoredAt).format('L LT')}`}
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Audit sheet */}
      <QaAuditSheet questions={callQa.questions} />

      {isManager && (
        <QaEditModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          callQa={callQa}
          airCallId={call?.airCallId}
        />
      )}
    </Stack>
  );
};

// "Edited by manager" - deliberately NOT purple (teal), so a human override reads
// differently from AI output. Hover shows AI original vs manager score.
const EditedByManagerPill = ({ qa }: { qa: CallQa }) => (
  <Tooltip title={
    <Stack>
      <span>AI original: {qa.qaScore}%</span>
      <span>Manager: {qa.overriddenScore}%</span>
      {qa.reviewedBy && <span>by {qa.reviewedBy}</span>}
      {qa.reviewedAt && <span>{moment(qa.reviewedAt).format('L LT')}</span>}
    </Stack>
  }>
    <Chip
      label={'Edited by manager'}
      size={'small'}
      sx={{ bgcolor: '#E2F3F1', color: '#0F766E', fontWeight: 600, fontSize: 11, height: 20, border: '1px solid #5EEAD4' }}
    />
  </Tooltip>
);